import React, { useState } from 'react';
import classNames from 'classnames';
import InputMask from 'react-input-mask';
import { useForm, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { format, parse } from 'date-fns';

import './RegisterRetail.scss';
import { RootState } from '../../../configs/store';
import Icon from 'src/components/Icon/Icon';

function RegisterRewards() {
  const { t } = useTranslation('common');
  const dispatch = useDispatch();
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const { emailStatus }  = useSelector((state: RootState) => state.auth);
  const { register, handleSubmit, watch, trigger, control, formState: { errors } } =
    useForm({ reValidateMode: 'onChange', mode: 'all', shouldFocusError: false });
  const onSubmit = data => {
    const date = parse(data.birthdate, 'dd/MM/yyyy', new Date());
    dispatch.auth.registerRewards({ ...data, birthdate: format(date, 'yyyy-MM-dd') });
  };

  const onEmailChange = (email) => {
    dispatch.auth.checkEmail(email);
  };

  const password = (label, name, width) => {
    return (
      <div
        className={
          classNames({
            'register-retail__input': true,
            'register-retail__input--icon': true,
            'register-retail__input--error': errors[name]
          })
        }
        style={ { width: `${ width }%` } }
      >
        <label className="register-retail__label">{ label }</label>
        <Icon
          icon="padlock"
          className="register-retail__icon"
        />
        <input
          type={ isPasswordVisible ? 'text' : 'password' }
          className="register-retail__password"
          {
            ...register(
              name,
              {
                required: 'This field is required',
                validate: (val) => {
                  if (watch('confirmPassword') !== val) {
                    return 'Your pin do not match';
                  }
                  if (name === 'confirmPassword') {
                    trigger('password');
                  }
                  return true;
                }
              }
            )
          }
        />
        {
          isPasswordVisible ?
            <Icon
              icon="eyeClosed"
              className="register-retail__icon register-retail__icon--right"
              onClick={ () => setIsPasswordVisible(false) }
            />
            :
            <Icon
              icon="eyeOpen"
              className="register-retail__icon register-retail__icon--right"
              onClick={ () => setIsPasswordVisible(true) }
            />
        }
        { errors[name] && <span className="register-retail--error-msg">{ errors[name].message }</span> }
      </div>
    );
  };

  const input = (label, name, type, width = 'auto', placeholder = null) => {
    return (
      <div
        className={
          classNames({
            'register-retail__input': true,
            'register-retail__input--error': errors[name]
          })
        }
        style={ { width: `${ width }%` } }
      >
        <label className="register-retail__label">{ label }</label>
        <input
          type={ type }
          className="register-retail__text"
          placeholder={ placeholder }
          {
            ...register(
              name,
              {
                required: 'This field is required',
                ...(name === 'email' && {
                  pattern: {
                    value: /\S+@\S+\.\S+/,
                    message: 'Entered value do not match email format'
                  },
                  validate: (val) => {
                    if (watch('confirmEmail') !== val) {
                      return 'Your email do not match';
                    }

                    return true;
                  },
                  onChange: (e) => onEmailChange(e.target.value)
                })
              }
            )
          }
        />
        { errors[name] && <span className="register-retail--error-msg">{ errors[name].message }</span> }
        { (emailStatus === 'not_available' && name === 'email') && <span className="register-retail--error-msg">{ t('form.emailInUse') }</span> }
      </div>
    );
  };

  const select = (label, name, width = 'auto', data = []) => {
    return (
      <div className="register-retail__input" style={ { width: `${ width }%` } }>
        <label className="register-retail__label">{ label }</label>
        <select
          className="register-retail__select"
          { ...register(name) }
        >
          { data.map((item, index) => <option key={ index } value={ item }>{ item }</option>) }
        </select>
      </div>
    );
  };

  const checkbox = (label, name) => {
    return (
      <div className="register-retail__divider register-retail__divider--no-border">
        <div className="register-retail__checkbox">
          <label className="register-retail__label register-retail__label--small">
            <input
              type="checkbox"
              { ...register(name) }
            />
            { label }
          </label>
        </div>
      </div>
    );
  };

  const phoneNumber = (label, name = '') => {
    const phone1 = `${ name }phone1` as any;
    const phone2 = `${ name }phone2` as any;

    return (
      <>
        <div
          className={
            classNames({
              'register-retail__input': true,
              'register-retail__input--error': errors[`${ name }phone1`] || errors[`${ name }phone2`]
            })
          }
          style={ { width: '50%' } }
        >
          <label className="register-retail__label">{ label }</label>
          <div className="register-retail__row register-retail__row--no-space">
            <input
              type="text"
              className="register-retail__text"
              style={ { width: '25%' } }
              placeholder="+27"
              {
                ...register(phone1, {
                  required: 'This field is required',
                  pattern: {
                    value: /^[+][1-9]{1,6}$/,
                    message: 'Entered value is not a valid code'
                  },
                  validate: (val) => {
                    if (watch('phone1') !== val) {
                      return 'Your phone number do not match';
                    }

                    return null;
                  } }
                )
              }
            />
            <input
              type="text"
              className="register-retail__text"
              style={ { width: '75%' } }
              placeholder="401228581"
              {
                ...register(phone2, {
                  required: 'This field is required',
                  validate: (val) => {
                    if (watch('phone2') !== val) {
                      return 'Your phone number do not match';
                    }

                    return null;
                  } }
                )
              }
            />
          </div>
          {
            (errors[`${ name }phone1`] || errors[`${ name }phone2`]) && <span className="register-retail--error-msg">
              { errors[`${ name }phone1`]?.message || errors[`${ name }phone2`]?.message }
            </span>
          }
        </div>
      </>
    );
  };

  const birthDate = () => {
    return (
      <div
        className={
          classNames({
            'register-retail__input': true,
            'register-retail__input--error': errors.birthdate
          })
        }
        style={ { width: '50%' } }
      >
        <label className="register-retail__label">{ t('form.birthdateLabel') }</label>
        <Controller
          name="birthdate"
          control={ control }
          { ...register('birthdate', { required: 'This field is required' }) }
          render={
            ({ field }) => (
              <InputMask
                mask="99/99/9999"
                value={ field.value }
                onChange={ field.onChange }
              >
                {
                  (inputProps: any) => (
                    <input
                      { ...inputProps }
                      className="register-retail__text"
                      type="text"
                    />
                  )
                }
              </InputMask>
            )
          }
        />
        { errors.birthdate && <span className="register-retail--error-msg">{ errors.birthdate.message }</span> }
      </div>
    );
  };

  return (
    <div className="register-retail">
      <h1 className="register-retail__title">Register</h1>
      <form className="register-retail__form" onSubmit={ handleSubmit(onSubmit) }>
        <div className="register-retail__divider">
          <div className="register-retail__row">
            { input(t('form.firstNameLabel'), 'firstName', 'text', '50', 'Your first name') }
            { input(t('form.lastNameLabel'), 'lastName', 'text', '50', 'Your last name') }
          </div>
          <div className="register-retail__row">
            { phoneNumber(t('form.phoneNumberLabel'), '') }
            { phoneNumber(t('form.confirmPhoneNumberLabel'), 'confirm') }
          </div>
          <div className="register-retail__row">
            { input(t('form.emailLabel'), 'email', 'email', '50') }
            { input(t('form.confirmEmailLabel'), 'confirmEmail', 'email', '50') }
          </div>
        </div>
        <div className="register-retail__divider">
          <div className="register-retail__row">
            { input(t('form.address'), 'address', 'text', '70') }
            { input(t('form.countryLabel'), 'country', 'text', '30') }
          </div>
          <div className="register-retail__row">
            { birthDate() }
            { input(t('form.idNumberLabel'), 'idNumber', 'number', '50') }
          </div>
          <div className="register-retail__row">
            { input(t('form.cardsNumber'), 'cardNumber', 'number', '40') }
            { password(t('form.createPinLabel'), 'password', '30') }
            { password(t('form.comfirmPinLabel'), 'confirmPassword', '30') }
          </div>
        </div>
        <div className="register-retail__divider register-retail__divider--no-border">
          { checkbox(t('form.newsletter'), 'newsletter') }
        </div>
        <div className="register-retail__divider register-retail__divider--no-border">
          { checkbox(t('form.newProducts'), 'newProducts') }
        </div>
        <div className="register-retail__divider register-retail__divider--no-border register-retail__divider--full-width">
          <span className="register-retail__required">{ t('form.indicatesRequiredField') }</span>
          <button className="register-retail__register modal-submit">{ t('register') }</button>
        </div>
      </form>
    </div>
  );
}

export default RegisterRewards;
